import React, { useState, useCallback, useMemo } from 'react';
import {
  Box,
  Paper,
  Typography,
  Chip,
  IconButton,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Grid,
  useTheme
} from '@mui/material';
import {
  DataGrid,
  GridColDef,
  GridRenderCellParams
} from '@mui/x-data-grid';
import {
  Visibility as VisibilityIcon,
  CheckCircle as CheckCircleIcon,
  Warning as WarningIcon,
  Error as ErrorIcon,
  Schedule as ScheduleIcon
} from '@mui/icons-material';
import { TeacherDashboardData } from '@/types/models';
import { dashboardStyles } from '@/layouts/TeacherLayout';

interface StudentsTabProps {
  data: TeacherDashboardData;
}

interface Application {
  id: number;
  opportunity: {
    title: string;
    organization: string;
  };
  status: string;
}

interface StudentRow {
  id: number;
  firstName: string;
  lastName: string;
  email: string;
  hoursCompleted: number;
  applications: Application[];
  careerGoals?: string;
  skills: string[];
  interests: string[];
}

const StudentsTab: React.FC<StudentsTabProps> = ({ data }) => {
  const [selectedStudent, setSelectedStudent] = useState<StudentRow | null>(null);
  const [paginationModel, setPaginationModel] = useState({
    pageSize: 10,
    page: 0,
  });
  const theme = useTheme();

  // Transform the data into a format that's easier to work with
  const rows = useMemo(() => {
    if (!data?.student_progress) return [];
    
    return data.student_progress.map(student => ({
      id: student.id,
      firstName: student.user?.first_name || '',
      lastName: student.user?.last_name || '',
      fullName: `${student.user?.first_name || ''} ${student.user?.last_name || ''}`.trim() || 'N/A',
      email: student.user?.email || 'N/A',
      hoursCompleted: student.progress?.hours_completed || 0,
      applications: student.progress?.applications || [],
      careerGoals: student.profile?.career_goals,
      skills: student.profile?.skills || [],
      interests: student.profile?.interests || [],
    }));
  }, [data?.student_progress]);

  const getStatusIcon = useCallback((status: string) => {
    switch (status.toLowerCase()) {
      case 'approved':
        return <CheckCircleIcon sx={{ color: theme.palette.success.main }} />;
      case 'pending':
        return <ScheduleIcon sx={{ color: theme.palette.warning.main }} />;
      case 'rejected':
        return <ErrorIcon sx={{ color: theme.palette.error.main }} />;
      default:
        return <WarningIcon sx={{ color: theme.palette.grey[500] }} />;
    }
  }, [theme]);

  const columns: GridColDef<StudentRow>[] = useMemo(() => [
    {
      field: 'fullName',
      headerName: 'Name',
      flex: 1,
      minWidth: 200,
      headerAlign: 'left',
      align: 'left',
    },
    {
      field: 'email',
      headerName: 'Email',
      flex: 1,
      minWidth: 200,
      headerAlign: 'left',
      align: 'left',
    },
    {
      field: 'hoursCompleted',
      headerName: 'Hours Completed',
      flex: 1,
      minWidth: 150,
      type: 'number',
      headerAlign: 'center',
      align: 'center',
    },
    {
      field: 'applications',
      headerName: 'Recent Applications',
      flex: 1,
      minWidth: 180,
      headerAlign: 'center',
      align: 'center',
      renderCell: (params: GridRenderCellParams<StudentRow>) => {
        const applications = params.row.applications || [];
        const recentApps = applications.slice(0, 3);
        
        return (
          <Box sx={{ 
            display: 'flex', 
            gap: 0.5,
            justifyContent: 'center',
            width: '100%'
          }}>
            {recentApps.map((app: Application) => (
              <Tooltip 
                key={app.id} 
                title={`${app.opportunity.title} - ${app.status}`}
              >
                {getStatusIcon(app.status)}
              </Tooltip>
            ))}
          </Box>
        );
      },
    },
    {
      field: 'actions',
      headerName: 'Actions',
      width: 100,
      headerAlign: 'center',
      align: 'center',
      renderCell: (params: GridRenderCellParams<StudentRow>) => (
        <Box sx={{ 
          display: 'flex', 
          justifyContent: 'center',
          width: '100%'
        }}>
          <IconButton 
            onClick={() => setSelectedStudent(params.row)}
            size="small"
          >
            <VisibilityIcon />
          </IconButton>
        </Box>
      ),
    },
  ], [getStatusIcon]);

  return (
    <Box sx={{ 
      height: '100%', 
      width: '100%', 
      display: 'flex', 
      flexDirection: 'column',
      gap: 2
    }}>
      <Paper 
        sx={{ 
          flex: 1,
          display: 'flex',
          flexDirection: 'column',
          p: { xs: 1, sm: 2, md: 3 },
          ...dashboardStyles.dashboard.analytics.card
        }}
      >
        <DataGrid<StudentRow>
          rows={rows}
          columns={columns}
          paginationModel={paginationModel}
          onPaginationModelChange={setPaginationModel}
          pageSizeOptions={[10, 25, 50]}
          disableRowSelectionOnClick
          autoHeight
          sx={{
            border: 'none',
            '& .MuiDataGrid-root': {
              backgroundColor: 'background.paper',
              borderRadius: 1,
            },
            '& .MuiDataGrid-cell': {
              borderBottom: 1,
              borderColor: 'divider',
              padding: { xs: 1, sm: 2 },
              fontSize: { xs: '0.875rem', sm: '1rem' },
              display: 'flex',
              alignItems: 'center',
              '&:focus': {
                outline: 'none'
              },
              '&:focus-within': {
                outline: 'none'
              }
            },
            '& .MuiDataGrid-columnHeaders': {
              backgroundColor: (theme) => theme.palette.grey[50],
              borderBottom: 2,
              borderColor: 'divider',
              minHeight: '56px',
              '& .MuiDataGrid-columnHeader': {
                padding: { xs: 1, sm: 2 },
                fontSize: { xs: '0.875rem', sm: '1rem' },
                fontWeight: 600,
                '&:focus': {
                  outline: 'none'
                },
                '&:focus-within': {
                  outline: 'none'
                },
                '& .MuiDataGrid-columnHeaderTitle': {
                  fontWeight: 600,
                  color: 'text.primary'
                }
              }
            },
            '& .MuiDataGrid-row': {
              '&:hover': {
                backgroundColor: (theme) => theme.palette.action.hover
              },
              '&:nth-of-type(even)': {
                backgroundColor: (theme) => theme.palette.action.hover + '20'
              },
              minHeight: '52px'
            },
            '& .MuiDataGrid-footerContainer': {
              borderTop: 2,
              borderColor: 'divider',
              backgroundColor: (theme) => theme.palette.grey[50],
              minHeight: '52px'
            },
            '& .MuiDataGrid-virtualScroller': {
              marginTop: '56px !important'
            },
            '& .MuiDataGrid-virtualScrollerContent': {
              minHeight: '52px !important'
            }
          }}
        />
      </Paper>

      <Dialog 
        open={!!selectedStudent} 
        onClose={() => setSelectedStudent(null)}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: { xs: 0, sm: 2 },
            margin: { xs: 0, sm: 2 },
            maxHeight: { xs: '100%', sm: 'calc(100% - 64px)' },
            height: { xs: '100%', sm: 'auto' }
          }
        }}
      >
        {selectedStudent && (
          <>
            <DialogTitle sx={{ 
              pb: 1,
              borderBottom: 1,
              borderColor: 'divider',
              backgroundColor: (theme) => theme.palette.grey[50]
            }}>
              <Typography variant="h6" sx={{ fontWeight: 600 }}>
                {selectedStudent.firstName} {selectedStudent.lastName}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {selectedStudent.email}
              </Typography>
            </DialogTitle>
            <DialogContent 
              dividers 
              sx={{ 
                p: { xs: 2, sm: 3 },
                backgroundColor: 'background.paper'
              }}
            >
              <Grid container spacing={3}>
                <Grid item xs={12} md={6}>
                  <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 600, color: 'primary.main' }}>
                    Profile Information
                  </Typography>
                  <Box sx={{ mb: 3 }}>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                      Career Goals
                    </Typography>
                    <Typography variant="body1" sx={{ 
                      p: 1.5, 
                      backgroundColor: 'grey.50',
                      borderRadius: 1,
                      minHeight: '3em'
                    }}>
                      {selectedStudent.careerGoals || 'Not specified'}
                    </Typography>
                  </Box>
                  <Box sx={{ mb: 3 }}>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                      Skills
                    </Typography>
                    <Box sx={{ 
                      display: 'flex', 
                      flexWrap: 'wrap', 
                      gap: 1,
                      p: 1.5,
                      backgroundColor: 'grey.50',
                      borderRadius: 1,
                      minHeight: '3em'
                    }}>
                      {selectedStudent.skills.map(skill => (
                        <Chip 
                          key={skill} 
                          label={skill} 
                          size="small"
                          color="primary"
                          variant="outlined"
                          sx={{ 
                            borderRadius: 1,
                            '& .MuiChip-label': {
                              px: 1
                            }
                          }}
                        />
                      ))}
                    </Box>
                  </Box>
                  <Box sx={{ mb: { xs: 3, md: 0 } }}>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                      Interests
                    </Typography>
                    <Box sx={{ 
                      display: 'flex', 
                      flexWrap: 'wrap', 
                      gap: 1,
                      p: 1.5,
                      backgroundColor: 'grey.50',
                      borderRadius: 1,
                      minHeight: '3em'
                    }}>
                      {selectedStudent.interests.map(interest => (
                        <Chip 
                          key={interest} 
                          label={interest} 
                          size="small"
                          color="secondary"
                          variant="outlined"
                          sx={{ 
                            borderRadius: 1,
                            '& .MuiChip-label': {
                              px: 1
                            }
                          }}
                        />
                      ))}
                    </Box>
                  </Box>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 600, color: 'primary.main' }}>
                    Progress & Activity
                  </Typography>
                  <Box sx={{ 
                    mb: 3,
                    p: 2,
                    backgroundColor: 'grey.50',
                    borderRadius: 1,
                    textAlign: 'center'
                  }}>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                      Total Hours Completed
                    </Typography>
                    <Typography variant="h4" sx={{ 
                      fontWeight: 600,
                      color: 'primary.main'
                    }}>
                      {selectedStudent.hoursCompleted}
                    </Typography>
                  </Box>
                  <Box>
                    <Typography variant="body2" color="text.secondary" gutterBottom>
                      Recent Applications
                    </Typography>
                    <Box sx={{ 
                      p: 1.5,
                      backgroundColor: 'grey.50',
                      borderRadius: 1,
                      minHeight: '3em'
                    }}>
                      {selectedStudent.applications.slice(0, 3).map(app => (
                        <Box 
                          key={app.id} 
                          sx={{ 
                            display: 'flex', 
                            alignItems: 'center', 
                            gap: 1,
                            mb: 1,
                            p: 1,
                            backgroundColor: 'background.paper',
                            borderRadius: 1,
                            '&:last-child': {
                              mb: 0
                            }
                          }}
                        >
                          {getStatusIcon(app.status)}
                          <Box>
                            <Typography variant="body2" sx={{ fontWeight: 500 }}>
                              {app.opportunity.title}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              {app.opportunity.organization}
                            </Typography>
                          </Box>
                        </Box>
                      ))}
                    </Box>
                  </Box>
                </Grid>
              </Grid>
            </DialogContent>
            <DialogActions sx={{ 
              p: 2,
              borderTop: 1,
              borderColor: 'divider',
              backgroundColor: (theme) => theme.palette.grey[50]
            }}>
              <Button 
                onClick={() => setSelectedStudent(null)}
                variant="contained"
                sx={{
                  borderRadius: 1,
                  textTransform: 'none',
                  px: 3
                }}
              >
                Close
              </Button>
            </DialogActions>
          </>
        )}
      </Dialog>
    </Box>
  );
};

export default StudentsTab; 